import axios from 'axios';
import {
  SelfReference,
  FamilyReference,
  GarageReference,
  StaffReference,
  CarModelReference,
  CarSpotReference,
  OtherReference,
  AllReferencesResponse,
  SelfPhotoType,
  ReferenceCategory,
  StaffStyle,
  CarModelStyle,
  SpotCategory,
} from '@/types';

const API_BASE = `${import.meta.env.VITE_AISHA_API_BASE || 'http://localhost:7999/api'}/user-references`;

/**
 * 全参照設定を取得
 */
export const fetchAllReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<AllReferencesResponse | null> => {
  try {
    const response = await axios.get(`${API_BASE}/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || '参照設定の取得に失敗しました');
    }
  } catch (err) {
    console.error('参照設定取得失敗', err);
    onError?.(err);
    return null;
  }
};

// ==================== 自分リファレンス ====================

/**
 * 自分リファレンス一覧を取得
 */
export const fetchSelfReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<SelfReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/self/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || '自分リファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('自分リファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * 自分リファレンスを作成・更新
 */
export const createOrUpdateSelfReference = async (
  userId: string,
  photoType: SelfPhotoType,
  photo?: File,
  onError?: (error: unknown) => void,
): Promise<SelfReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('photo_type', photoType);
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await axios.post(`${API_BASE}/self/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || '自分リファレンスの保存に失敗しました');
    }
  } catch (err) {
    console.error('自分リファレンス作成・更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * 自分リファレンスを削除
 */
export const deleteSelfReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/self/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('自分リファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};

// ==================== ファミリーリファレンス ====================

/**
 * ファミリーリファレンス一覧を取得
 */
export const fetchFamilyReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<FamilyReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/family/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || 'ファミリーリファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('ファミリーリファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * ファミリーリファレンスを作成
 */
export const createFamilyReference = async (
  userId: string,
  familyName: string,
  photo?: File,
  onError?: (error: unknown) => void,
): Promise<FamilyReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('family_name', familyName);
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await axios.post(`${API_BASE}/family/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'ファミリーリファレンスの作成に失敗しました');
    }
  } catch (err) {
    console.error('ファミリーリファレンス作成失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * ファミリーリファレンスを更新
 */
export const updateFamilyReference = async (
  id: number,
  data: { familyName?: string; photo?: File },
  onError?: (error: unknown) => void,
): Promise<FamilyReference | null> => {
  try {
    const formData = new FormData();
    if (data.familyName) {
      formData.append('family_name', data.familyName);
    }
    if (data.photo) {
      formData.append('photo', data.photo);
    }

    const response = await axios.put(`${API_BASE}/family/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'ファミリーリファレンスの更新に失敗しました');
    }
  } catch (err) {
    console.error('ファミリーリファレンス更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * ファミリーリファレンスを削除
 */
export const deleteFamilyReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/family/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('ファミリーリファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};

// ==================== ガレージリファレンス ====================

/**
 * ガレージリファレンス一覧を取得
 */
export const fetchGarageReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<GarageReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/garage/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || 'ガレージリファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('ガレージリファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * ガレージリファレンスを作成
 */
export const createGarageReference = async (
  userId: string,
  garageName: string,
  photo?: File,
  onError?: (error: unknown) => void,
): Promise<GarageReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('garage_name', garageName);
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await axios.post(`${API_BASE}/garage/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'ガレージリファレンスの作成に失敗しました');
    }
  } catch (err) {
    console.error('ガレージリファレンス作成失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * ガレージリファレンスを更新
 */
export const updateGarageReference = async (
  id: number,
  data: { garageName?: string; photo?: File },
  onError?: (error: unknown) => void,
): Promise<GarageReference | null> => {
  try {
    const formData = new FormData();
    if (data.garageName) {
      formData.append('garage_name', data.garageName);
    }
    if (data.photo) {
      formData.append('photo', data.photo);
    }

    const response = await axios.put(`${API_BASE}/garage/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'ガレージリファレンスの更新に失敗しました');
    }
  } catch (err) {
    console.error('ガレージリファレンス更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * ガレージリファレンスを削除
 */
export const deleteGarageReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/garage/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('ガレージリファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};

// ==================== スタッフリファレンス ====================

/**
 * スタッフリファレンス一覧を取得
 */
export const fetchStaffReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<StaffReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/staff/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || 'スタッフリファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('スタッフリファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * スタッフリファレンスを作成
 */
export const createStaffReference = async (
  userId: string,
  staffName: string,
  style: StaffStyle,
  photo?: File,
  displayImage?: File,
  onError?: (error: unknown) => void,
): Promise<StaffReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('staff_name', staffName);
    formData.append('style', style);
    if (photo) {
      formData.append('photo', photo);
    }
    if (displayImage) {
      formData.append('display_image', displayImage);
    }

    const response = await axios.post(`${API_BASE}/staff/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'スタッフリファレンスの作成に失敗しました');
    }
  } catch (err) {
    console.error('スタッフリファレンス作成失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * スタッフリファレンスを更新
 */
export const updateStaffReference = async (
  id: number,
  data: { staff_name?: string; style?: StaffStyle; photo?: File; display_image?: File },
  onError?: (error: unknown) => void,
): Promise<StaffReference | null> => {
  try {
    const formData = new FormData();
    if (data.staff_name) {
      formData.append('staff_name', data.staff_name);
    }
    if (data.style) {
      formData.append('style', data.style);
    }
    if (data.photo) {
      formData.append('photo', data.photo);
    }
    if (data.display_image) {
      formData.append('display_image', data.display_image);
    }

    const response = await axios.put(`${API_BASE}/staff/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'スタッフリファレンスの更新に失敗しました');
    }
  } catch (err) {
    console.error('スタッフリファレンス更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * スタッフリファレンスを削除
 */
export const deleteStaffReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/staff/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('スタッフリファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};

// ==================== カーモデルリファレンス ====================

/**
 * カーモデルリファレンス一覧を取得
 */
export const fetchCarModelReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<CarModelReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/car-model/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || 'カーモデルリファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('カーモデルリファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * カーモデルリファレンスを作成
 */
export const createCarModelReference = async (
  userId: string,
  modelName: string,
  style: CarModelStyle,
  photo?: File,
  displayImage?: File,
  onError?: (error: unknown) => void,
): Promise<CarModelReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('model_name', modelName);
    formData.append('style', style);
    if (photo) {
      formData.append('photo', photo);
    }
    if (displayImage) {
      formData.append('display_image', displayImage);
    }

    const response = await axios.post(`${API_BASE}/car-model/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'カーモデルリファレンスの作成に失敗しました');
    }
  } catch (err) {
    console.error('カーモデルリファレンス作成失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * カーモデルリファレンスを更新
 */
export const updateCarModelReference = async (
  id: number,
  data: { model_name?: string; style?: CarModelStyle; photo?: File; display_image?: File },
  onError?: (error: unknown) => void,
): Promise<CarModelReference | null> => {
  try {
    const formData = new FormData();
    if (data.model_name) {
      formData.append('model_name', data.model_name);
    }
    if (data.style) {
      formData.append('style', data.style);
    }
    if (data.photo) {
      formData.append('photo', data.photo);
    }
    if (data.display_image) {
      formData.append('display_image', data.display_image);
    }

    const response = await axios.put(`${API_BASE}/car-model/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'カーモデルリファレンスの更新に失敗しました');
    }
  } catch (err) {
    console.error('カーモデルリファレンス更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * カーモデルリファレンスを削除
 */
export const deleteCarModelReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/car-model/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('カーモデルリファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};

// ==================== カースポットリファレンス ====================

/**
 * カースポットリファレンス一覧を取得
 */
export const fetchCarSpotReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<CarSpotReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/car-spot/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || 'カースポットリファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('カースポットリファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * カースポットリファレンスを作成
 */
export const createCarSpotReference = async (
  userId: string,
  spotName: string,
  category: SpotCategory,
  photo?: File,
  displayImage?: File,
  onError?: (error: unknown) => void,
): Promise<CarSpotReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('spot_name', spotName);
    formData.append('category', category);
    if (photo) {
      formData.append('photo', photo);
    }
    if (displayImage) {
      formData.append('display_image', displayImage);
    }

    const response = await axios.post(`${API_BASE}/car-spot/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'カースポットリファレンスの作成に失敗しました');
    }
  } catch (err) {
    console.error('カースポットリファレンス作成失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * カースポットリファレンスを更新
 */
export const updateCarSpotReference = async (
  id: number,
  data: { spot_name?: string; category?: SpotCategory; photo?: File; display_image?: File },
  onError?: (error: unknown) => void,
): Promise<CarSpotReference | null> => {
  try {
    const formData = new FormData();
    if (data.spot_name) {
      formData.append('spot_name', data.spot_name);
    }
    if (data.category) {
      formData.append('category', data.category);
    }
    if (data.photo) {
      formData.append('photo', data.photo);
    }
    if (data.display_image) {
      formData.append('display_image', data.display_image);
    }

    const response = await axios.put(`${API_BASE}/car-spot/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'カースポットリファレンスの更新に失敗しました');
    }
  } catch (err) {
    console.error('カースポットリファレンス更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * カースポットリファレンスを削除
 */
export const deleteCarSpotReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/car-spot/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('カースポットリファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};

// ==================== その他リファレンス ====================

/**
 * その他リファレンス一覧を取得
 */
export const fetchOtherReferences = async (
  userId: string,
  onError?: (error: unknown) => void,
): Promise<OtherReference[]> => {
  try {
    const response = await axios.get(`${API_BASE}/other/`, {
      params: { user_id: userId },
    });
    if (response.data.success) {
      return response.data.data || [];
    } else {
      throw new Error(response.data.error || 'その他リファレンスの取得に失敗しました');
    }
  } catch (err) {
    console.error('その他リファレンス取得失敗', err);
    onError?.(err);
    return [];
  }
};

/**
 * その他リファレンスを作成
 */
export const createOtherReference = async (
  userId: string,
  referenceName: string,
  category: ReferenceCategory,
  photo?: File,
  onError?: (error: unknown) => void,
): Promise<OtherReference | null> => {
  try {
    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('reference_name', referenceName);
    formData.append('category', category);
    if (photo) {
      formData.append('photo', photo);
    }

    const response = await axios.post(`${API_BASE}/other/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'その他リファレンスの作成に失敗しました');
    }
  } catch (err) {
    console.error('その他リファレンス作成失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * その他リファレンスを更新
 */
export const updateOtherReference = async (
  id: number,
  data: { referenceName?: string; category?: ReferenceCategory; photo?: File },
  onError?: (error: unknown) => void,
): Promise<OtherReference | null> => {
  try {
    const formData = new FormData();
    if (data.referenceName) {
      formData.append('reference_name', data.referenceName);
    }
    if (data.category) {
      formData.append('category', data.category);
    }
    if (data.photo) {
      formData.append('photo', data.photo);
    }

    const response = await axios.put(`${API_BASE}/other/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'その他リファレンスの更新に失敗しました');
    }
  } catch (err) {
    console.error('その他リファレンス更新失敗', err);
    onError?.(err);
    return null;
  }
};

/**
 * その他リファレンスを削除
 */
export const deleteOtherReference = async (
  id: number,
  onError?: (error: unknown) => void,
): Promise<boolean> => {
  try {
    const response = await axios.delete(`${API_BASE}/other/${id}/`);
    return response.data.success;
  } catch (err) {
    console.error('その他リファレンス削除失敗', err);
    onError?.(err);
    return false;
  }
};
